import { ExpandableRadioOption } from '@distribusion/owl-frontend-ui'
import unique from 'lodash/uniq'
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'

import { PaymentMethodSetting, PaymentProvidersMethodsSetting } from '@api/settings'
import { PAYMENT_PROVIDERS } from '@constants/paymentProviders'
import { PaymentMethodStatus } from '@enums'
import { FinishPaymentParams, PaymentEvents } from '@hooks/useBookingFlow'
import useMoovitIntegration from '@hooks/useMoovitIntegration'
import utils from '@lib/utils'
import { useAdyenMethods } from '@pages/Checkout/hooks/Payment/Adyen/useAdyenMethods'
import { useAlipayMethods } from '@pages/Checkout/hooks/Payment/Alipay/useAlipayMethods'
import { useCheckoutComMethods } from '@pages/Checkout/hooks/Payment/CheckoutCom/useCheckoutComMethods'
import { useDelayedMethods } from '@pages/Checkout/hooks/Payment/Delayed/useDelayedMethods'
import { useDLocalMethods } from '@pages/Checkout/hooks/Payment/dLocal/useDlocalMethods'
import { useMercadoPagoMethods } from '@pages/Checkout/hooks/Payment/Mercadopago/useMercadoPagoMethods'
import { useOtherMethods } from '@pages/Checkout/hooks/Payment/Other/useOtherMethods'
import { useOxMethods } from '@pages/Checkout/hooks/Payment/Ox/useOxMethods'
import { useTransdevMethods } from '@pages/Checkout/hooks/Payment/Transdev/useTransdevMethods'
import { useVGSMethods } from '@pages/Checkout/hooks/Payment/VGS/useVGSMethods'
import { usePluginContext } from '@plugins/index'
import { useSettings } from '@queries/settings'

export type OptionType = ExpandableRadioOption<PaymentMethodType>

export interface PaymentMethod<TEvents extends PaymentEvents = PaymentEvents> {
  getOption: () => OptionType | OptionType[] | null | undefined
  status: PaymentMethodStatus
  provider?: string
  extraContent?: ReactNode
  getPayButton?: () => ReactElement
  error?: Error | null
  isLoading?: boolean
  isSubmitting?: boolean
  on?: TEvents
}

export type MethodMap = Record<string, PaymentMethod>
type ProvidersMap = Record<PaymentProvider | 'other', MethodMap>

interface PaymentMethodsHookResult {
  available: PaymentMethod[]
  selected?: PaymentMethod | null
}

interface PaymentMethodsHookParams {
  methodType?: PaymentMethodType | null
  confirmationOnly?: boolean
  finishPayment: (params: FinishPaymentParams) => void
  error?: Error | null
}

const DEFAULT_METHODS_ORDER = [
  'savedCreditCard',
  'ideal',
  'dana',
  'gcash',
  'pix',
  'applePay',
  'googlePay',
  'nativeGooglePay',
  'paypal',
  'mp',
  'creditCard',
  'checkoutcomFlow',
  'storedPayment',
  'blik',
  'sepa_direct_debit',
  'holdReservation',
  'terminal',
  'cash',
  'invoice',
  'ox',
  'fallbackCreditCard',
]

export const useBookingPayment = ({
  methodType,
  confirmationOnly,
  finishPayment,
  error,
}: PaymentMethodsHookParams): PaymentMethodsHookResult => {
  const [{ paymentProvidersMethods, holdBooking, amtrackSavedCards }] = useSettings()
  const selectedMethodRef = useRef<PaymentMethod | null>()
  const { hooks } = usePluginContext()
  const methodsOrder = unique([...(hooks.usePaymentMethodsOrder?.() || []), ...DEFAULT_METHODS_ORDER])

  const alipay = useAlipayMethods()
  const adyen = useAdyenMethods({ selectedMethod: selectedMethodRef, finishPayment, error })
  const checkoutcom = useCheckoutComMethods()
  const vgs = useVGSMethods()
  const delayed = useDelayedMethods()
  const dlocal = useDLocalMethods()
  const other = useOtherMethods()
  const transdev = useTransdevMethods()
  const moovit = useMoovitIntegration()
  const oxxo_direct = useOxMethods()
  const mercadopago = useMercadoPagoMethods()
  const fallbackProvider = hooks.useFallbackPayment?.()

  const extendedMethodSettings = useMemo<Record<string, PaymentMethodSetting>>(() => {
    const methodsCamel = utils.object.convertToCamelCase<PaymentProvidersMethodsSetting>(paymentProvidersMethods)

    return {
      ...methodsCamel,
      savedCreditCard: amtrackSavedCards.enabled ? { provider: PAYMENT_PROVIDERS.VGS } : null,
      holdReservation: !confirmationOnly && holdBooking.enabled ? { provider: null } : null,
      nativeGooglePay: moovit.enabled && !!methodsCamel.googlePay ? { provider: PAYMENT_PROVIDERS.ADYEN } : null,
      googlePay: !moovit.enabled && !!methodsCamel.googlePay ? { provider: PAYMENT_PROVIDERS.ADYEN } : null,
      fallbackCreditCard: fallbackProvider ? { provider: PAYMENT_PROVIDERS.FALLBACK_PROVIDER } : null,
      // creditCard: { provider: PAYMENT_PROVIDERS.CHECKOUTCOM },
    }
  }, [
    amtrackSavedCards.enabled,
    confirmationOnly,
    fallbackProvider,
    holdBooking.enabled,
    moovit.enabled,
    paymentProvidersMethods,
  ])

  const providers = useMemo<ProvidersMap>(
    () => ({
      adyen,
      alipay,
      checkoutcom,
      delayed,
      dlocal,
      vgs,
      transdev,
      other,
      oxxo_direct,
      mercadopago,
      fallbackProvider,
    }),
    [adyen, alipay, checkoutcom, delayed, dlocal, other, vgs, transdev, fallbackProvider, oxxo_direct, mercadopago],
  )
  const findMethod = useCallback(
    (paymentMethodType: PaymentMethodType): PaymentMethod | undefined => {
      const type = utils.string.toCamelcase(paymentMethodType.split(':')[0]) as keyof PaymentProvidersMethodsSetting
      const setting = extendedMethodSettings[type]

      return setting ? providers[setting.provider ?? 'other']?.[type] : undefined
    },
    [extendedMethodSettings, providers],
  )

  const availableMethods = useMemo(() => {
    return methodsOrder.map(type => findMethod(type)).filter((method): method is PaymentMethod => !!method)
  }, [findMethod])

  const selectedMethod = useMemo(() => (methodType ? findMethod(methodType) : null), [findMethod, methodType])

  useEffect(() => {
    selectedMethodRef.current = selectedMethod
  }, [selectedMethod])

  return { available: availableMethods, selected: selectedMethod }
}
